import React, { useEffect, useState } from "react";
import { useDoctor } from "../context/DoctorContext";
import { useNotification } from "../context/NotificationContext";
import { User } from "lucide-react";
import axios from "axios";
import {
  handleApiResponse,
  handleApiError,
} from "../utils/notificationHelpers";

// All field configurations
const formFields = [
  // Read-only fields
  { name: "name", label: "Name", type: "text", editable: false },
  { name: "dob", label: "Date of Birth", type: "date", editable: false },
  { name: "gender", label: "Gender", type: "text", editable: false },
  { name: "college", label: "College", type: "text", editable: false },
  { name: "degree", label: "Degree", type: "text", editable: false },
  {
    name: "year_of_completion",
    label: "Year of Completion",
    type: "number",
    editable: false,
  },
  {
    name: "license_number",
    label: "License Number",
    type: "text",
    editable: false,
  },
  {
    name: "years_of_experience",
    label: "Years of Experience",
    type: "number",
    editable: false,
  },
  {
    name: "aadhaar_number",
    label: "Aadhaar Number",
    type: "text",
    editable: false,
  },
  { name: "pan_number", label: "PAN Number", type: "text", editable: false },

  // Editable fields
  {
    name: "weekly_routine",
    label: "Weekly Routine",
    type: "text",
    editable: true,
  },
  {
    name: "specialisation",
    label: "Specialisation",
    type: "text",
    isArray: true,
    editable: true,
  },
  {
    name: "phone",
    label: "Phone Number",
    type: "tel",
    editable: true,
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    editable: true,
  },
  {
    name: "country_code",
    label: "Country Code",
    type: "text",
    editable: true,
  },
  {
    name: "state",
    label: "State",
    type: "text",
    editable: true,
  },
  {
    name: "district",
    label: "District",
    type: "text",
    editable: true,
  },
  {
    name: "languages_known",
    label: "Languages Known",
    type: "text",
    isArray: true,
    editable: true,
  },
  {
    name: "religion",
    label: "Religion",
    type: "text",
    editable: true,
  },
];

const Profile = () => {
  const { doctor, setDoctor, isLoading: contextLoading } = useDoctor();
  const { notify } = useNotification();
  const [isUpdating, setIsUpdating] = useState(false);
  const [formData, setFormData] = useState({});

  // Initialize form data when doctor data is available
  useEffect(() => {
    if (doctor) {
      const initialData = formFields.reduce((acc, field) => {
        // Ensure all fields have a defined value, even if empty
        acc[field.name] = doctor[field.name] ?? (field.isArray ? [] : "");
        return acc;
      }, {});
      // Also initialize address fields
      initialData.current_address = doctor.current_address ?? "";
      initialData.permanent_address = doctor.permanent_address ?? "";
      setFormData(initialData);
    } else {
      // Initialize with empty values if no doctor data
      const emptyData = formFields.reduce((acc, field) => {
        acc[field.name] = field.isArray ? [] : "";
        return acc;
      }, {});
      emptyData.current_address = "";
      emptyData.permanent_address = "";
      setFormData(emptyData);
    }
  }, [doctor]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleArrayInputChange = (e) => {
    const { name, value } = e.target;
    const values = value.split(",").map((item) => item.trim());
    setFormData((prev) => ({
      ...prev,
      [name]: values,
    }));
  };

  const updateDoctorProfile = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      const doctorId = doctor?.id;
      if (!doctorId) {
        notify.error("Profile update failed");
        return;
      }

      // Only include editable fields in the update
      const editableData = Object.entries(formData).reduce(
        (acc, [key, value]) => {
          const field = formFields.find((f) => f.name === key);
          if (field?.editable) {
            acc[key] = value;
          }
          return acc;
        },
        {}
      );

      const response = await axios.patch(
        `https://api.rezen.in/api/v1/doctors/${doctorId}/public`,
        editableData
      );

      if (
        handleApiResponse(
          response,
          notify,
          "Profile updated",
          "Profile update failed"
        )
      ) {
        // Update the doctor context with the new data while preserving existing data
        const updatedDoctor = {
          ...doctor,
          ...editableData,
        };
        setDoctor(updatedDoctor);

        // Update localStorage with the new data
        localStorage.setItem("doctorData", JSON.stringify(updatedDoctor));
      }
    } catch (error) {
      handleApiError(error, notify, "profile");
    } finally {
      setIsUpdating(false);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      name: field.name,
      value: field.isArray
        ? formData[field.name]?.join(", ")
        : formData[field.name],
      onChange: field.isArray ? handleArrayInputChange : handleInputChange,
      disabled: !field.editable,
      className: `w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
        !field.editable ? "bg-gray-50 text-gray-600 cursor-not-allowed" : ""
      }`,
    };

    return (
      <div key={field.name} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
        </label>
        {field.name.includes("address") ? (
          <textarea {...commonProps} rows="3" />
        ) : (
          <input type={field.type} {...commonProps} />
        )}
      </div>
    );
  };

  if (contextLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full padding-x mt-10 mb-24 lg:mt-20 lg:mb-36 max-w-none lg:max-w-screen-xl mx-0 lg:mx-auto">
      <div className="bg-white rounded-xl shadow-sm p-6 lg:p-8">
        {/* Profile Header */}
        <div className="flex flex-col items-center mb-8">
          {doctor?.profile_image ? (
            <img
              src={doctor.profile_image}
              alt={doctor.name || "Doctor profile"}
              className="h-32 w-32 rounded-full object-cover mb-4"
            />
          ) : (
            <div className="h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center mb-4">
              <User className="h-16 w-16 text-gray-400" />
            </div>
          )}
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-800">
            {doctor?.name || "Doctor"}
          </h1>
        </div>

        {/* Profile Form */}
        <form
          onSubmit={updateDoctorProfile}
          className="max-w-3xl mx-auto space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formFields.map(renderField)}
          </div>

          {/* Address Fields */}
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Current Address
              </label>
              <textarea
                name="current_address"
                value={formData.current_address}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                rows="3"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Permanent Address
              </label>
              <textarea
                name="permanent_address"
                value={formData.permanent_address}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                rows="3"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <button
              type="submit"
              disabled={isUpdating}
              className={`px-8 py-3 rounded-md text-white font-medium text-lg ${
                isUpdating
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-orange-500 hover:bg-orange-600"
              } transition-colors shadow-sm hover:shadow-md`}
            >
              {isUpdating ? "Updating..." : "Update Profile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
